import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Plus, Send, Trash2, Wallet, FileMinus, FileCode2, Radio, BellRing, Star, Mail, Eye, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { studio, apiError, beDate, eur, inputCls, btnPrimary, btnGhost, VAT_RATES } from "@/lib/studioApi";
import { useFiscal } from "@/lib/fiscal";
import { FiscalPanel } from "@/components/studio/FiscalPanel";
import { PdfPreviewDialog } from "@/components/studio/PdfPreviewDialog";
import { PageSkeleton } from "@/components/studio/StudioUI";
import { peppolMeta } from "@/components/studio/PeppolBadge";
import { InvoiceSendModal } from "@/components/studio/InvoiceSendModal";
import { ClientQuestions } from "@/components/studio/ClientQuestions";
import { AccountantQuestions } from "@/components/studio/AccountantQuestions";

const emptyLine = { description: "", quantity: 1, unit: "forfait", unit_price: 0, vat_percent: 21, reverse_charge: false };
const TEMPLATE_LABEL = { envoi: "Envoi", relance: "Relance", avis: "Demande d'avis", perso: "Personnalisé" };

export default function StudioInvoice() {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [profile, setProfile] = useState({});
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(false);
  const [sendModal, setSendModal] = useState(null);
  const [payment, setPayment] = useState({ amount: "", method: "virement", date: "" });
  const [fiscalForm, setFiscalForm] = useState({ client_country: "", client_type: "", client_vat: "", fiscal_confirmed: false });
  const fiscalRules = useFiscal();

  const load = () => studio.get(`/invoices/${id}`).then(({ data }) => {
    setInvoice(data); setLines(data.lines || []);
    setFiscalForm({ client_country: data.client_country || "", client_type: data.client_type || "", client_vat: data.client_vat || "", fiscal_confirmed: !!(data.fiscal && data.fiscal.confirmed), fiscal: data.fiscal });
  });

  useEffect(() => {
    studio.get("/whoami").then(({ data }) => {
      if (!data.is_owner) return;
      load().catch(() => toast.error("Facture introuvable."));
      studio.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    }).catch(() => {});
  }, [id]);

  const vatRates = fiscalRules?.rates || VAT_RATES[profile.vat_country || "BE"] || VAT_RATES.BE;
  const totals = lines.reduce((acc, l) => {
    const net = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
    acc.net += net;
    acc.vat += l.reverse_charge ? 0 : (net * (Number(l.vat_percent) || 0)) / 100;
    return acc;
  }, { net: 0, vat: 0 });

  const save = async () => {
    setBusy("save");
    try {
      await studio.put(`/invoices/${id}`, {
        lines: lines.map((l) => ({
          description: l.description, quantity: Number(l.quantity) || 0, unit: l.unit,
          unit_price: Number(l.unit_price) || 0, vat_percent: l.reverse_charge ? 0 : Number(l.vat_percent) || 0,
          reverse_charge: !!l.reverse_charge,
        })),
        client_country: fiscalForm.client_country, client_type: fiscalForm.client_type, client_vat: fiscalForm.client_vat, fiscal_confirmed: !!fiscalForm.fiscal_confirmed,
      });
      await load();
      toast.success("Facture enregistrée.");
    } catch (e) { toast.error(apiError(e, "Enregistrement impossible.")); }
    finally { setBusy(""); }
  };

  const act = async (key, path, body, done, method = "post") => {
    setBusy(key);
    try {
      const { data } = method === "get" ? await studio.get(path) : await studio.post(path, body || {});
      await load();
      done?.(data);
    } catch (e) { toast.error(apiError(e, "Action impossible.")); }
    finally { setBusy(""); }
  };

  const download = async (kind) => {
    setBusy(kind);
    try {
      const res = await studio.get(`/invoices/${id}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoice.reference}${kind === "ubl" ? "-ubl.xml" : kind === "verifactu.xml" ? "-verifactu.xml" : ".pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Téléchargement impossible."); }
    finally { setBusy(""); }
  };

  if (!invoice) return <StudioLayout ownerOnly title="Facture"><PageSkeleton testId="invoice-skeleton" /></StudioLayout>;

  const locked = invoice.status === "payee" || invoice.kind === "avoir";

  return (
    <StudioLayout ownerOnly title={`${invoice.kind_label} ${invoice.reference}`} subtitle={`${invoice.client_name} · ${invoice.status_label} · échéance ${beDate(invoice.due_date)}`}>
      <Link to="/studio/factures" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#5C6A87] hover:text-[#3A5CC8] transition-colors duration-200">
        <ArrowLeft className="w-4 h-4" /> Toutes les factures
      </Link>

      <ClientQuestions docId={invoice.id} docType="facture" />
      <AccountantQuestions invoiceId={invoice.id} canReply emptyHidden />

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Total HTVA", eur(totals.net)], ["TVA", eur(totals.vat)], ["Total TVAC", eur(totals.net + totals.vat)], ["Solde dû", eur(invoice.balance)]].map(([l, v], i) => (
          <div key={l} className={`rounded-2xl border p-4 ${i === 3 ? "border-[#3A5CC8] bg-[#EEF1FC]" : "border-[#E2E7F7] bg-white"}`}>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">{l}</p>
            <p className="mt-1 font-heading text-lg font-bold text-[#0F2645]">{v}</p>
          </div>
        ))}
      </div>

      {invoice.quote_number && (
        <p className="mt-4 text-sm text-[#5C6A87]">
          Rattachée au devis <span className="font-semibold text-[#0F2645]">{invoice.quote_number}</span>
          {invoice.deposit_invoice_id ? " · acompte déjà facturé déduit" : ""}
        </p>
      )}

      {invoice.status !== "payee" && <div className="mt-6"><FiscalPanel value={fiscalForm} onChange={setFiscalForm} /></div>}
      <div data-testid="invoice-lines" className="mt-6 space-y-3">
        {lines.map((l, i) => (
          <div key={i} className="rounded-2xl border border-[#E2E7F7] bg-white p-4 space-y-2 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <textarea
              data-testid={`invoice-line-desc-${i}`}
              rows={2}
              disabled={locked}
              className={inputCls}
              placeholder="Description"
              value={l.description}
              onChange={(e) => setLines((p) => p.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))}
            />
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <input data-testid={`invoice-line-qty-${i}`} disabled={locked} type="number" step="0.01" className={inputCls} value={l.quantity} onChange={(e) => setLines((p) => p.map((x, idx) => idx === i ? { ...x, quantity: e.target.value } : x))} />
              <input data-testid={`invoice-line-unit-${i}`} disabled={locked} className={inputCls} value={l.unit} onChange={(e) => setLines((p) => p.map((x, idx) => idx === i ? { ...x, unit: e.target.value } : x))} />
              <input data-testid={`invoice-line-price-${i}`} disabled={locked} type="number" step="0.01" className={inputCls} value={l.unit_price} onChange={(e) => setLines((p) => p.map((x, idx) => idx === i ? { ...x, unit_price: e.target.value } : x))} />
              <select
                data-testid={`invoice-line-vat-${i}`}
                disabled={locked}
                className={inputCls}
                value={l.reverse_charge ? "ac" : String(l.vat_percent)}
                onChange={(e) => {
                  const v = e.target.value;
                  setLines((p) => p.map((x, idx) => idx === i
                    ? (v === "ac" ? { ...x, reverse_charge: true, vat_percent: 0 } : { ...x, reverse_charge: false, vat_percent: v })
                    : x));
                }}
              >
                {vatRates.map((v) => <option key={v} value={v}>{`${v} % TVA`}</option>)}
                <option value="ac">Autoliquidation</option>
              </select>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-[#0F2645]">{eur((Number(l.quantity) || 0) * (Number(l.unit_price) || 0))}</span>
                {!locked && (
                  <button type="button" data-testid={`invoice-line-delete-${i}`} onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))} className="p-1 text-slate-300 hover:text-red-600 transition-colors duration-200">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {!locked && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" data-testid="invoice-add-line" onClick={() => setLines([...lines, { ...emptyLine, vat_percent: fiscalRules?.standard_rate ?? 21 }])} className={btnGhost}>
            <Plus className="w-3.5 h-3.5" /> Ajouter une ligne
          </button>
          <button type="button" data-testid="invoice-save" disabled={busy === "save"} onClick={save} className={btnPrimary}>
            {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enregistrer
          </button>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <p className="font-heading text-base font-semibold text-[#0F2645]">Envoyer et documents</p>
          <div data-testid="invoice-action-group" className="mt-3 flex flex-col gap-2">
            <button type="button" data-testid="invoice-send" onClick={() => setSendModal("envoi")} className={`${btnPrimary} h-12 w-full text-base sm:h-11 sm:text-sm`}>
              <Send className="w-4 h-4" /> Envoyer au client
            </button>
            {invoice.balance > 0.5 && invoice.kind !== "avoir" && invoice.sent_at && (
              <button type="button" data-testid="invoice-reminder" onClick={() => setSendModal("relance")} className={`${btnGhost} h-12 w-full sm:h-11`}>
                <BellRing className="w-4 h-4 text-[#3A5CC8]" /> Relancer le client
              </button>
            )}
            <div className="grid grid-cols-2 gap-2">
            {invoice.kind !== "avoir" && invoice.sent_at && (
              <button type="button" data-testid="invoice-review-request" onClick={() => setSendModal("avis")} className={`${btnGhost} h-12 sm:h-11`}>
                <Star className="w-4 h-4 text-amber-500" /> Demander un avis
              </button>
            )}
            <button type="button" data-testid="invoice-preview" onClick={() => setPreview(true)} className={`${btnGhost} h-12 sm:h-11`}>
              <Eye className="w-4 h-4 text-[#3A5CC8]" /> Visualiser
            </button>
            <button type="button" data-testid="invoice-pdf" onClick={() => download("pdf")} className={`${btnGhost} h-12 sm:h-11`}>
              <Download className="w-4 h-4 text-[#3A5CC8]" /> PDF
            </button>
            {fiscalRules?.einvoicing === "peppol" && (
            <button type="button" data-testid="invoice-ubl" onClick={() => download("ubl")} className={`${btnGhost} h-12 sm:h-11`}>
              <FileCode2 className="w-4 h-4 text-[#3A5CC8]" /> Peppol (UBL)
            </button>
            )}
            {fiscalRules?.einvoicing === "peppol" && !invoice.peppol?.document_id && (
              <button
                type="button"
                data-testid="invoice-peppol"
                disabled={busy === "peppol"}
                onClick={() => act("peppol", `/invoices/${id}/peppol`, {}, () => toast.success("Facture transmise via le réseau Peppol."))}
                className={`${btnGhost} h-12 sm:h-11`}
              >
                {busy === "peppol" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4 text-[#3A5CC8]" />} Envoyer via Peppol
              </button>
            )}
            </div>
          </div>
          {invoice.payment_declared_at && invoice.balance > 0.5 && (
            <div data-testid="invoice-payment-declared" className="mt-2 inline-flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
              Le client indique avoir payé ({beDate(invoice.payment_declared_at.slice(0, 10))}) — vérifiez votre compte puis enregistrez le paiement.
            </div>
          )}
          {invoice.verifactu?.huella && (
            <div data-testid="invoice-verifactu-status" className="mt-2 inline-flex flex-wrap items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800">
              <ShieldCheck className="w-3.5 h-3.5" /> Verifactu · registro n.º {invoice.verifactu.seq} · huella {invoice.verifactu.huella.slice(0, 12)}…
              <button type="button" data-testid="invoice-verifactu-xml" disabled={busy === "verifactu.xml"} onClick={() => download("verifactu.xml")} className="underline underline-offset-2 hover:text-emerald-950">Descargar registro XML</button>
            </div>
          )}
          {fiscalRules?.country === "FR" && invoice.status !== "brouillon" && (
            <p data-testid="invoice-facturx-hint" className="mt-2 inline-flex items-center gap-2 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] px-3 py-2 text-xs font-semibold text-[#0F2645]"><ShieldCheck className="w-3.5 h-3.5 text-[#3A5CC8]" /> PDF Factur-X (EN 16931) : XML de facturation électronique intégré au PDF, prêt pour votre plateforme agréée.</p>
          )}
          {invoice.peppol?.document_id && (
            <div data-testid="invoice-peppol-status" className={`mt-2 inline-flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold ${peppolMeta(invoice.peppol.status).cls}`}>
              <Radio className="w-3.5 h-3.5" /> Peppol : {peppolMeta(invoice.peppol.status).label} · {invoice.peppol.address} · {beDate(invoice.peppol.sent_at)}
              {invoice.peppol.failure_reason && <span className="font-normal">— {invoice.peppol.failure_reason}</span>}
              <button
                type="button"
                data-testid="invoice-peppol-refresh"
                disabled={busy === "peppol-refresh"}
                onClick={() => act("peppol-refresh", `/invoices/${id}/peppol/status`, null, () => toast.success("Statut Peppol actualisé."), "get")}
                className="ml-1 underline decoration-dotted underline-offset-2 hover:opacity-80"
              >
                Actualiser
              </button>
            </div>
          )}
          {!invoice.client_vat && !invoice.peppol?.document_id && (
            <p data-testid="invoice-peppol-hint" className="mt-2 text-xs text-[#5C6A87]">Peppol : réservé aux clients professionnels avec un numéro de TVA (ajoutez-le sur la fiche client).</p>
          )}
          {invoice.reminder_count > 0 && (
            <p data-testid="invoice-reminder-info" className="mt-2 text-xs font-semibold text-amber-700">
              {invoice.reminder_count} rappel(s) envoyé(s) — dernier le {beDate((invoice.reminder_sent_at || "").slice(0, 10))}
            </p>
          )}
          {!!invoice.emails?.length && (
            <div data-testid="invoice-email-history" className="mt-3 space-y-1.5 border-t border-[#E2E7F7] pt-3">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#93A0BC]">{invoice.emails.length} email{invoice.emails.length > 1 ? "s" : ""} envoyé{invoice.emails.length > 1 ? "s" : ""}</p>
              {[...invoice.emails].reverse().slice(0, 5).map((m, i) => (
                <p key={i} className="flex items-center gap-2 text-xs text-[#5C6A87]">
                  <Mail className="h-3 w-3 shrink-0 text-[#3A5CC8]" />
                  <span className="font-semibold text-[#0F2645]">{TEMPLATE_LABEL[m.template] || m.template}</span>
                  <span className="truncate">→ {m.to.join(", ")}</span>
                  <span className="ml-auto shrink-0">{beDate((m.at || "").slice(0, 10))}</span>
                </p>
              ))}
            </div>
          )}
          {invoice.viewed_at && (
            <p data-testid="invoice-viewed" className="mt-3 text-xs font-semibold text-indigo-700">
              Consultée par le client le {beDate(invoice.viewed_at.slice(0, 10))} ({invoice.view_count} ouverture{invoice.view_count > 1 ? "s" : ""})
            </p>
          )}
        </div>

        <div className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <p className="font-heading text-base font-semibold text-[#0F2645] inline-flex items-center gap-2">
            <Wallet className="w-4 h-4 text-[#3A5CC8]" /> Règlements
          </p>
          <div className="mt-3 space-y-1">
            {(invoice.payments || []).map((p) => (
              <p key={p.id} className="text-sm text-[#5C6A87]">{beDate(p.date)} · {eur(p.amount)} · {p.method}</p>
            ))}
            {!invoice.payments?.length && <p className="text-sm text-[#5C6A87]">Aucun règlement enregistré.</p>}
          </div>
          {invoice.kind !== "avoir" && invoice.status !== "payee" && (
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <input data-testid="payment-amount" type="number" step="0.01" className={inputCls} placeholder={`Montant (solde ${eur(invoice.balance)})`} value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: e.target.value })} />
              <select data-testid="payment-method" className={inputCls} value={payment.method} onChange={(e) => setPayment({ ...payment, method: e.target.value })}>
                <option value="virement">Virement</option>
                <option value="bancontact">Bancontact</option>
                <option value="especes">Espèces</option>
                <option value="carte">Carte</option>
              </select>
              <button
                type="button"
                data-testid="payment-add"
                disabled={busy === "pay" || !payment.amount}
                onClick={() => act("pay", `/invoices/${id}/payments`, { amount: Number(payment.amount), method: payment.method }, () => { setPayment({ ...payment, amount: "" }); toast.success("Règlement enregistré."); })}
                className={`${btnPrimary} whitespace-nowrap`}
              >
                {busy === "pay" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Encaisser
              </button>
            </div>
          )}
          {invoice.kind !== "avoir" && (
            <button
              type="button"
              data-testid="invoice-credit-note"
              disabled={busy === "credit"}
              onClick={() => window.confirm("Créer une note de crédit pour annuler cette facture ?") && act("credit", `/invoices/${id}/credit-note`, {}, (d) => toast.success(`Note de crédit ${d.reference} créée.`))}
              className={`${btnGhost} mt-3`}
            >
              <FileMinus className="w-3.5 h-3.5" /> Créer une note de crédit
            </button>
          )}
          {invoice.credit_note_reference && (
            <p className="mt-2 text-xs font-semibold text-[#5C6A87]">Note de crédit liée : {invoice.credit_note_reference}</p>
          )}
        </div>
      </div>

      {sendModal && (
        <InvoiceSendModal
          invoiceId={id}
          reference={invoice.reference}
          defaultTemplate={sendModal}
          onClose={() => setSendModal(null)}
          onSent={() => load()}
        />
      )}
      <PdfPreviewDialog open={preview} onClose={() => setPreview(false)} url={`/invoices/${id}/pdf`} filename={`facture-${invoice.number || invoice.reference || id}.pdf`} title={`Facture ${invoice.number || invoice.reference || ""} — aperçu client`} editLabel="Retour à la facture" testId="invoice-pdf-preview" />
    </StudioLayout>
  );
}
